import React from 'react';
import { Helmet } from 'react-helmet';
import SimpleLayout from '@/layouts/SimpleLayout';
import { useSolutionModals } from '@/hooks/useSolutionModals';
import HeroSection from './components/HeroSection';
import MetricsSection from './components/MetricsSection';
import ProductIntroSection from './components/ProductIntroSection';
import ProblemsSection from './components/ProblemsSection';
import CoreFeaturesSection from './components/CoreFeaturesSection';
import FloorMapDetailSection from './components/FloorMapDetailSection';
import AIConciergeDetailSection from './components/AIConciergeDetailSection';
import MoreFeaturesSection from './components/MoreFeaturesSection';
import WhyChatbotSection from './components/WhyChatbotSection';
import CaseStudySection from './components/CaseStudySection';
import FAQSection from './components/FAQSection';
import CTASection from './components/CTASection';
import VideoModal from './components/VideoModal';

/** Solution页面专用 FAQ Schema */
const FAQ_SCHEMA = {
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: [
    {
      '@type': 'Question',
      name: '百貨店のフロアマップ連携は可能ですか？',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'はい、既存のフロアマップ画像や3Dマップデータを取り込み、現在地から目的地までのルート案内が可能です。エレベーター・エスカレーター優先など、お客様に合わせたルート設定もできます。',
      },
    },
    {
      '@type': 'Question',
      name: 'テナント情報の更新は簡単ですか？',
      acceptedAnswer: {
        '@type': 'Answer',
        text: '各テナントのWebサイトやInstagram、PDFカタログを自動でクロールし、最新情報を常に反映します。新店舗オープンや閉店情報も、管理画面から即座に反映可能です。',
      },
    },
    {
      '@type': 'Question',
      name: 'インバウンド（訪日外国人）対応はできますか？',
      acceptedAnswer: {
        '@type': 'Answer',
        text: '英語、中国語（簡体・繁体）、韓国語、タイ語、ベトナム語など10言語以上に自動対応。免税カウンターの場所、外貨両替、Wi-Fi情報なども多言語で案内できます。',
      },
    },
    {
      '@type': 'Question',
      name: 'AIの回答精度はどの程度ですか？',
      acceptedAnswer: {
        '@type': 'Answer',
        text: '導入時に施設の情報を学習させることで、高い精度で回答できます。運用中も管理画面から回答内容を確認・修正でき、継続的に精度を向上させることが可能です。',
      },
    },
    {
      '@type': 'Question',
      name: '専用端末・キオスク筐体は必要ですか？',
      acceptedAnswer: {
        '@type': 'Answer',
        text: '不要です。iPad、既存のサイネージ、またはお客様自身のスマートフォン（LINE/Webブラウザ）で動作します。高額な専用端末への投資なしで導入できます。',
      },
    },
    {
      '@type': 'Question',
      name: '導入までの期間はどれくらいですか？',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'テナント情報（PDF/Web）を読み込ませるだけで、最短1週間で構築完了。従来のシステムと比較して、導入期間を大幅に短縮できます。14日間の無料トライアルもご用意しています。',
      },
    },
  ],
};

/** Solution页面专用 SoftwareApplication Schema */
const SOFTWARE_SCHEMA = {
  '@context': 'https://schema.org',
  '@type': 'SoftwareApplication',
  name: 'GBase Support',
  alternateName: '商業施設向けAIチャットボット',
  applicationCategory: 'BusinessApplication',
  applicationSubCategory: 'AI Chatbot, AI Concierge',
  operatingSystem: 'Web, iOS, Android',
  offers: {
    '@type': 'Offer',
    price: '0',
    priceCurrency: 'JPY',
    availability: 'https://schema.org/InStock',
    url: 'https://cs.gbase.ai/register',
    description: '14日間無料トライアル',
  },
  aggregateRating: {
    '@type': 'AggregateRating',
    ratingValue: '4.8',
    reviewCount: '50',
    bestRating: '5',
    worstRating: '1',
  },
  description:
    '商業施設・百貨店向けAIチャットボット。フロアマップ連携、10言語対応のAIコンシェルジュ機能で案内業務を自動化。',
  keywords: 'AIチャットボット, チャットボット, AIコンシェルジュ, フロアマップ, 商業施設, 店舗案内',
  screenshot: 'https://cs.gbase.ai/assets/管理画面.png',
  featureList: [
    'フロアマップ連携ナビゲーション',
    'AIコンシェルジュ機能',
    '10言語以上の多言語チャットボット',
    'リアルタイム在庫確認',
    'FAQ自動対応',
    '専用端末不要',
    '最短1週間で導入',
  ],
  browserRequirements: 'Requires JavaScript. Requires HTML5.',
  softwareVersion: '2.0',
  datePublished: '2024-01-01',
  author: {
    '@type': 'Organization',
    name: 'Sparticle Inc.',
    url: 'https://sparticle.com',
  },
};

/** Solution页面专用 BreadcrumbList Schema */
const BREADCRUMB_SCHEMA = {
  '@context': 'https://schema.org',
  '@type': 'BreadcrumbList',
  itemListElement: [
    { '@type': 'ListItem', position: 1, name: 'ホーム', item: 'https://cs.gbase.ai/' },
    { '@type': 'ListItem', position: 2, name: '商業施設向けソリューション', item: 'https://cs.gbase.ai/solution' },
  ],
};

/**
 * Solution页面
 * GBase Support产品展示页面
 *
 * 页面结构:
 * 1. HeroSection - 英雄区域,Live Demo入口（链接到kiosk-floating页面）
 * 2. MetricsSection - KPI指标展示
 * 3. ProblemsSection - 5大課題展示
 * 4. CoreFeaturesSection - 核心3機能展示
 * 5. MoreFeaturesSection - 其他強力機能
 * 6. CaseStudySection - NEWoMan高輪導入事例
 * 7. FAQSection - 百貨店・商業施設向けよくあるご質問
 * 8. CTASection - 最終行動召喚
 *
 * Modal组件:
 * - VideoModal - 视频演示Modal
 */
const SolutionPage: React.FC = () => {
  const { videoModal, openVideoModal, closeVideoModal } = useSolutionModals();

  return (
    <SimpleLayout>
      {/* Solution页面专用 SEO meta 和结构化数据 */}
      <Helmet>
        <title>AIチャットボット for 商業施設 - フロアマップ連携で案内対応50~70%削減｜GBase Support</title>
        <meta
          name="description"
          content="AIチャットボット「GBase Support」は商業施設・百貨店向けのAIコンシェルジュ。フロアマップ連携で店舗案内を自動化、10言語対応で案内所対応50~70%削減。専用端末不要、最短1週間導入。無料トライアル実施中。"
        />
        <meta
          name="keywords"
          content="AIチャットボット, チャットボット, AIコンシェルジュ, フロアマップ, 商業施設, 百貨店, 店舗案内, 多言語対応, インバウンド, 案内所自動化"
        />
        <meta property="og:title" content="商業施設向けAIチャットボットで案内対応50~70%削減｜GBase Support" />
        <meta
          property="og:description"
          content="百貨店・商業施設向けAIコンシェルジュ。フロアマップ連携で店舗案内を自動化。10言語対応、案内所対応50~70%削減。専用端末不要で最短1週間導入可能。"
        />
        <meta property="og:url" content="https://cs.gbase.ai/solution" />
        <meta property="og:type" content="website" />
        <script type="application/ld+json">{JSON.stringify(FAQ_SCHEMA)}</script>
        <script type="application/ld+json">{JSON.stringify(SOFTWARE_SCHEMA)}</script>
        <script type="application/ld+json">{JSON.stringify(BREADCRUMB_SCHEMA)}</script>
      </Helmet>

      <div className="font-solution bg-solution-bg">
        {/* Hero区域 */}
        <HeroSection />

        {/* KPI指标 */}
        <MetricsSection />

        {/* 产品介绍 (SEO) */}
        <ProductIntroSection />

        {/* 5大課題 */}
        <ProblemsSection />

        {/* 核心3機能 */}
        <CoreFeaturesSection onOpenVideo={openVideoModal} />

        {/* フロアマップ連携详细 (SEO) */}
        <FloorMapDetailSection />

        {/* AIコンシェルジュ详细 (SEO) */}
        <AIConciergeDetailSection />

        {/* 其他強力機能 */}
        <MoreFeaturesSection />

        {/* なぜチャットボットが必要か (SEO) */}
        <WhyChatbotSection />

        {/* 導入事例 */}
        <CaseStudySection onOpenVideo={openVideoModal} />

        {/* FAQ */}
        <FAQSection />

        {/* 最終CTA */}
        <CTASection />

        {/* Modal组件 */}
        <VideoModal isOpen={videoModal.isOpen} src={videoModal.src} onClose={closeVideoModal} />
      </div>
    </SimpleLayout>
  );
};

export default SolutionPage;
